import { motion } from "framer-motion";
import {
  ClipboardList,
  DatabaseBackup,
  Globe2,
  KeyRound,
  Lock,
  Network,
  Server,
  Shield,
  ShieldCheck,
} from "lucide-react";
import PulsingPillIcon from "./PulsingPillIcon";
import { slowFadeUp, slowStagger } from "../utils/animations";

const securityBlockGap = "mt-8 md:mt-10";
const securityCardPadding = "p-5 md:p-7 lg:p-8";
const securityFeatureArticleClass =
  "flex flex-row items-start gap-3 px-4 py-5 md:px-5 md:py-6 transition hover:-translate-y-1 hover:shadow-md";
const securityFeatureGridClass =
  "grid grid-cols-1 divide-y divide-slate-200 md:divide-y-0 md:divide-x";

export default function Security() {
  const securityPillars = [
    {
      title: "Secure by Design",
      detail: "Security is built into every layer of our platform.",
      iconAccent: "text-blue-600",
      Icon: ShieldCheck,
    },
    {
      title: "Privacy First",
      detail: "We protect patient data with strict privacy controls.",
      iconAccent: "text-emerald-600",
      Icon: Lock,
    },
    {
      title: "Highly Reliable",
      detail: "99.9% uptime with robust infrastructure and backups.",
      iconAccent: "text-violet-600",
      Icon: Server,
    },
    {
      title: "Access Controlled",
      detail: "Role-based access ensures the right users see the right data.",
      iconAccent: "text-amber-600",
      Icon: KeyRound,
    },
  ];

  const compliance = [
    {
      label: "HIPAA Compliant",
      shortLabel: "HIPAA",
      featured: true,
      accent:
        "border-blue-300/50 bg-blue-500/25 text-blue-50 shadow-blue-500/20 hover:border-blue-200/70 hover:bg-blue-500/35",
    },
    {
      label: "ISO 27001:2013 Certified",
      shortLabel: "ISO 27001",
      featured: true,
      accent:
        "border-emerald-300/50 bg-emerald-500/25 text-emerald-50 shadow-emerald-500/20 hover:border-emerald-200/70 hover:bg-emerald-500/35",
    },
    {
      label: "ISO 27799:2016 Certified",
      shortLabel: "ISO 27799",
      accent:
        "border-violet-200/70 bg-violet-500/20 text-violet-50 hover:bg-violet-500/30",
    },
    {
      label: "GDPR Compliant",
      shortLabel: "GDPR",
      accent:
        "border-amber-200/70 bg-amber-500/20 text-amber-50 hover:bg-amber-500/30",
    },
    {
      label: "HL7 Compliant",
      shortLabel: "HL7",
      accent:
        "border-cyan-200/70 bg-cyan-500/20 text-cyan-50 hover:bg-cyan-500/30",
    },
    {
      label: "FHIR Compliant",
      shortLabel: "FHIR",
      accent:
        "border-rose-200/70 bg-rose-500/20 text-rose-50 hover:bg-rose-500/30",
    },
  ];

  const protectionLayers = [
    {
      title: "Network Security",
      text: "Firewalls, DDoS protection, and intrusion detection.",
      accent: "text-blue-600",
      Icon: Network,
    },
    {
      title: "Data Encryption",
      text: "End-to-end encryption at transit and at rest.",
      accent: "text-emerald-600",
      Icon: Lock,
    },
    {
      title: "Identity & Access Management",
      text: "MFA and role-based controls for secure access.",
      accent: "text-violet-600",
      Icon: KeyRound,
    },
    {
      title: "Backup & Disaster Recovery",
      text: "Automated backups and fast recovery for continuity.",
      accent: "text-amber-600",
      Icon: DatabaseBackup,
    },
    {
      title: "Audit & Monitoring",
      text: "Comprehensive logs and monitoring for transparency.",
      accent: "text-cyan-600",
      Icon: ClipboardList,
    },
  ];

  return (
    <section
      id="security"
      className="section-viewport scroll-mt-24 py-12 md:py-20 bg-slate-50 px-4 md:px-6"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 text-center md:mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
            <PulsingPillIcon icon={Shield} tone="blue" />
            Security & Compliance
          </span>
        </div>

        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="mx-auto max-w-3xl text-section font-bold text-secondary leading-tight">
              <span className="block">Built for Healthcare.</span>
              <span className="block bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
                Enterprise-Grade Security.
              </span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-7 text-muted md:mt-5">
              We follow global standards and best practices to keep your data
              secure, private, and always compliant.
            </p>
          </div>

          <motion.div
            variants={slowStagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className={`${securityBlockGap} ${securityFeatureGridClass} md:grid-cols-4`}
          >
            {securityPillars.map((pillar) => {
                const PillarIcon = pillar.Icon;
                return (
                  <motion.article
                    variants={slowFadeUp}
                    key={pillar.title}
                    className={`${securityFeatureArticleClass} md:flex-col md:items-stretch md:gap-0`}
                  >
                    <PillarIcon
                      className={`h-5 w-5 shrink-0 ${pillar.iconAccent}`}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <h4 className={`text-base font-semibold md:mt-3 ${pillar.iconAccent}`}>
                        {pillar.title}
                      </h4>
                      <p className="mt-1.5 text-sm text-muted leading-6 md:mt-2">
                        {pillar.detail}
                      </p>
                    </div>
                  </motion.article>
                );
            })}
          </motion.div>

        </div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className={`relative isolate overflow-hidden rounded-2xl ${securityBlockGap} border border-white/10 text-white shadow-xl shadow-slate-900/25 transition hover:-translate-y-1 hover:shadow-2xl hover:shadow-slate-900/30`}
        >
          <div
            className="absolute inset-0 bg-cover bg-[position:70%_center] opacity-35"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/compliance-standards-bg.jpg)`,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-blue-950"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-blue-500/25 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-28 -left-24 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_80%_at_0%_50%,rgba(59,130,246,0.22),transparent_55%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:28px_28px] [mask-image:radial-gradient(ellipse_90%_80%_at_70%_50%,black,transparent)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-6 left-[42%] hidden w-px bg-gradient-to-b from-transparent via-white/15 to-transparent lg:block"
            aria-hidden
          />
          <div className={`relative z-10 ${securityCardPadding}`}>
            <div className="grid grid-cols-1 items-center gap-6 md:gap-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:gap-10">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-blue-100 backdrop-blur-sm">
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                  Certifications
                </span>
                <h3 className="font-display mt-3 text-2xl font-bold tracking-tight text-white md:mt-4 md:text-[1.75rem] lg:text-3xl [text-shadow:0_2px_16px_rgba(0,0,0,0.35)]">
                  Compliant with Global Healthcare Standards
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-200/95 md:text-base">
                  We adhere to industry-leading standards and regulations to ensure
                  the highest level of protection and interoperability.
                </p>
                <dl className="mt-5 grid grid-cols-2 gap-2.5 md:mt-6 md:gap-3">
                  <div className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 backdrop-blur-sm">
                    <dt className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                      Standards
                    </dt>
                    <dd className="font-display text-lg font-bold tabular-nums text-white">
                      6+
                    </dd>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 backdrop-blur-sm">
                    <dt className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
                      Focus
                    </dt>
                    <dd className="text-sm font-semibold text-blue-100">
                      Healthcare data
                    </dd>
                  </div>
                </dl>
              </div>

              <motion.div
                variants={slowStagger}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
                className="grid auto-rows-fr grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4 lg:gap-3"
              >
                {compliance.map((item) => (
                  <motion.div
                    variants={slowFadeUp}
                    key={item.label}
                    className={[
                      "group flex flex-col items-center justify-center rounded-xl border p-3 text-center shadow-lg backdrop-blur-md transition duration-300 hover:-translate-y-0.5 hover:shadow-xl",
                      item.featured
                        ? "col-span-1 row-span-2 min-h-[9rem] lg:col-span-2 lg:min-h-[8.5rem] lg:p-5"
                        : "min-h-[4.25rem]",
                      item.accent,
                    ].join(" ")}
                  >
                    <ShieldCheck
                      className="mb-2 h-4 w-4 opacity-70 transition group-hover:opacity-100"
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="font-display text-sm font-bold leading-tight sm:hidden">
                      {item.shortLabel}
                    </span>
                    <span className="hidden font-display text-sm font-bold leading-snug sm:block lg:text-base">
                      {item.featured ? item.label : item.shortLabel}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className={securityBlockGap}
        >
          <h3 className="font-display mb-5 text-2xl font-bold tracking-tight text-secondary md:mb-6">
            Multiple Layers of Security Protection
          </h3>
          <div className={`${securityFeatureGridClass} md:grid-cols-2 xl:grid-cols-5`}>
            {protectionLayers.map((layer) => {
              const LayerIcon = layer.Icon;
              return (
                <article
                  key={layer.title}
                  className={`${securityFeatureArticleClass} xl:flex-col xl:items-stretch xl:gap-0`}
                >
                  <LayerIcon
                    className={`h-5 w-5 shrink-0 ${layer.accent}`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className={`text-base font-semibold xl:mt-3 ${layer.accent}`}>
                      {layer.title}
                    </h4>
                    <p className="mt-1.5 text-sm text-muted leading-6 xl:mt-2">
                      {layer.text}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className={`relative isolate overflow-hidden ${securityBlockGap} rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-900/5 transition hover:shadow-xl`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 lg:items-stretch">
            <div className="relative isolate overflow-hidden text-white lg:col-span-5">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-40"
                style={{
                  backgroundImage: `url(${process.env.PUBLIC_URL}/images/security-trust-bg.jpg)`,
                }}
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900/95 to-blue-950"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl"
                aria-hidden
              />
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_70%_at_20%_80%,rgba(59,130,246,0.2),transparent_55%)]"
                aria-hidden
              />

              <div className={`relative z-10 flex h-full flex-col justify-center ${securityCardPadding}`}>
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-blue-100 backdrop-blur-sm">
                  <Lock className="h-3.5 w-3.5" aria-hidden />
                  Trust & care
                </span>
                <div className="mt-3 space-y-1 md:mt-4">
                  <h3 className="font-display text-2xl font-bold tracking-tight md:text-[1.65rem]">
                    Your Data. Your Trust.
                  </h3>
                  <p className="font-display text-2xl font-bold tracking-tight text-blue-100/95 md:text-[1.65rem]">
                    Our Responsibility.
                  </p>
                </div>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-200/95 md:mt-4 md:text-[0.9375rem]">
                  We maintain the highest standards of security and compliance so you
                  can focus on what matters most — patient care.
                </p>
              </div>
            </div>

            <div className={`flex flex-col justify-center border-t border-slate-200 lg:col-span-7 lg:border-l lg:border-t-0 ${securityCardPadding}`}>
              <div className="flex items-start gap-3 md:gap-4">
                <div className="min-w-0 flex-1">
                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-slate-600">
                    <Globe2 className="h-3.5 w-3.5 text-blue-600" aria-hidden />
                    Global footprint
                  </span>
                  <h3 className="font-display mt-3 text-xl font-bold tracking-tight text-secondary md:mt-4 md:text-2xl">
                    Data Residency & Sovereignty
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted md:text-base">
                    Compliant with all applicable data security law requirements across
                    every region we serve.
                  </p>
                </div>
                <div
                  className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 lg:flex"
                  aria-hidden
                >
                  <Globe2 className="h-6 w-6" strokeWidth={1.75} />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
